import React, { useRef, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import PropTypes from 'prop-types';
import Button from '../../atoms/Button/Button';
import styles from './UploadInput.css';
import useTranslate from '../../../hooks/useTranslate';
import * as config from '../../../helpers/config';
import { authHeader } from '../../../api/transport';
import SVG from '../../atoms/SVG/SVG';
import { createToast } from '../../../store/actions/toast';
import { ERROR } from '../../../constants/toastType';
import FieldError from '../Error/FieldError';
import { tokenSelector } from '../../../store/selectors/token';
import { useSelector } from 'react-redux';
import { currentTaskSelector } from '../../../store/selectors/task';

const uploadFile = async (file, token, taskId) => {
  const formData = new FormData();
  formData.append('MultipartFile', file);
  formData.append('IdTask', taskId);
  // @TODO écart backend v5, confirmé dans le code source : endpoints.upload vaut
  // https://localhost:6007/api/Upload dans les trois environnements v5 déployés (dev, test et
  // prod), donc l'envoi de fichier ne peut de toute façon pas atteindre le bon serveur.
  // Mais même en corrigeant cette URL vers le vrai endpoint (POST
  // {{backendUrl}}/TaskService/Upload, Task.Api/Controllers/UploadController.cs, qui accepte
  // bien les champs "IdTask" et un fichier comme envoyés ici), la fonctionnalité resterait
  // cassée : la logique d'upload (stockage Azure Blob) y est commentée, et la méthode renvoie
  // toujours "https://www.google.com" en dur, quel que soit le fichier envoyé. Ce n'est donc
  // pas une mauvaise configuration front, l'upload n'est pas implémenté côté backend v5.
  // This call does not go through the fetch middleware, so it builds its own auth header
  const response = await fetch(config.get('endpoints.upload'), {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: authHeader(token),
    },
  });
  if (response.status === 500) {
    throw new TypeError('download failed');
  }

  if (response.headers.get('Content-Type')?.includes('application/json')) {
    return await response.json();
  }

  throw new TypeError('Expected text response.');
};

// Validation uses megabytes (binary)
const MEGABYTES_BYTES = 1048576;

function UploadInput({ mimetypes, value, onChange, sizeRange, readonly }) {
  const i18n = useTranslate();
  const dropzoneRef = useRef(null);
  const [error, setError] = useState(null);
  // Initialize with value, if any
  const [currentFile, setCurrentFile] = useState(value ? { name: value } : null);
  const [uploadState, setUploadState] = useState('pending');
  const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    accept: mimetypes?.length
      ? Object.fromEntries(mimetypes.map((mime) => [mime, []]))
      : undefined,
    disabled: readonly,
  });

  const token = useSelector(tokenSelector);
  const currentTask = useSelector(currentTaskSelector);
  const currentTaskCopy = { ...currentTask };

  useEffect(() => {
    setCurrentFile(acceptedFiles[acceptedFiles.length - 1]);
  }, [acceptedFiles]);

  // Upload file to server handler
  useEffect(() => {
    const handleUpload = async () => {
      if (!currentFile) {
        setUploadState('pending');
        currentTaskCopy.isUploading = false;
        return;
      }

      // Validation part
      // Since we send the value in state only when upload succeded
      if (sizeRange) {
        const fileSize = currentFile.size;

        if (fileSize < sizeRange.min * MEGABYTES_BYTES) {
          setError(i18n('UploadInput.errors.belowMin', { size: sizeRange.min }));
          currentTaskCopy.isUploading = false;
          return;
        } else if (fileSize > sizeRange.max * MEGABYTES_BYTES) {
          setError(i18n('UploadInput.errors.aboveMax', { size: sizeRange.max }));
          currentTaskCopy.isUploading = false;
          return;
        }
      }

      // Upload part
      if (currentFile) {
        setUploadState('uploading');
        currentTaskCopy.isUploading = true;
        try {
          const fileUrl = await uploadFile(currentFile, token, currentTaskCopy?.taskId);
          onChange(fileUrl);
          setUploadState('done');
          currentTaskCopy.isUploading = false;
        } catch (err) {
          setUploadState('pending');
          currentTaskCopy.isUploading = false;
          setCurrentFile(null);
          createToast(ERROR, i18n('UploadInput.uploadFailed', { message: err.message }));
        }
      }
    };
    handleUpload();
  }, [currentFile, token, currentTaskCopy?.taskId]);

  const clearFile = () => {
    setCurrentFile(null);
    setError(null);
    onChange(null);
  };

  return (
    <div className={styles.UploadInput}>
      <div className={styles.UploadInput__input}>
        <div
          {...getRootProps({ className: 'dropzone' })}
          className={styles.UploadInput__dropzone}
          ref={dropzoneRef}
        >
          <input {...getInputProps()} />
          <Button
            icon="field_upload"
            type="button"
            className={styles.UploadInput__button}
            disabled={readonly}
          >
            {i18n('UploadInput.openDialog')}
          </Button>
        </div>
        <div className={styles.UploadInput__preview}>
          {currentFile &&
            (uploadState === 'uploading' ? (
              <>
                <SVG glyph="spinner" className={styles.UploadInput__spinner} />
                <span className={styles.UploadInput__fileMeta}>{i18n('UploadInput.pending')}</span>
              </>
            ) : (
              <>
                <Button
                  icon="close"
                  type="button"
                  flat
                  onClick={clearFile}
                  className={styles.UploadInput__clearButton}
                />
                <span className={styles.UploadInput__fileMeta}>{currentFile.name}</span>
              </>
            ))}
        </div>
      </div>
      {error && <FieldError message={error} />}
    </div>
  );
}

UploadInput.propTypes = {
  mimetypes: PropTypes.array,
  value: PropTypes.string,
  onChange: PropTypes.func,
  sizeRange: PropTypes.object,
  readonly: PropTypes.bool,
};

export default UploadInput;
